"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MultiSelect = MultiSelect;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_dom_1 = require("react-dom");
const lucide_react_1 = require("lucide-react");
const cn_1 = require("../lib/cn");
const folded_1 = require("../lib/folded");
const overlay_1 = require("../lib/overlay");
const truncated_text_1 = require("./truncated-text");
const select_all_bar_1 = require("./select-all-bar");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 16 Oct 2026 · `Select`, choosing SEVERAL — the report builder's filters.  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The same trigger, the same portalled list and the same search as `Select`**, so a rail of
 * filters reads as one kind of control whichever each row is. What differs is a tick: it toggles
 * the option and the list STAYS OPEN — three projects is three ticks, not three trips.
 *
 * ⚠️ **A `listbox` with `aria-multiselectable`**, each option `aria-selected` — the pattern a screen
 * reader announces as a multi-select, rather than a menu of checkboxes it would read as actions.
 *
 * ⚠️ The trigger names the first choice and how many more (*Haystack +2*), as the list toolbar's
 * chip does.
 */
function MultiSelect({ options, values, onValuesChange, placeholder, label, searchable = false, searchPlaceholder, emptyLabel, id, className, disabled, inline = false, checkboxes = false, rangeSelect = false, hint, clearLabel, summarize, panelWidth, selectAllLabel, deselectAllLabel, }) {
    const listId = (0, react_1.useId)();
    const [open, setOpen] = (0, react_1.useState)(false);
    const [query, setQuery] = (0, react_1.useState)('');
    const [active, setActive] = (0, react_1.useState)(0);
    /** `rangeSelect` · the value last clicked without Shift — where the next range starts. */
    const anchor = (0, react_1.useRef)(null);
    const close = (0, react_1.useCallback)(() => {
        setOpen(false);
        setQuery('');
    }, []);
    const { surface, trigger } = (0, overlay_1.useDismiss)(open, close);
    const { rect } = (0, overlay_1.useAnchorRect)(open, trigger);
    const shown = (0, react_1.useMemo)(() => {
        const needle = (0, folded_1.folded)(query.trim());
        if (!searchable || needle === '')
            return options;
        return options.filter((option) => (0, folded_1.folded)(option.label).includes(needle));
    }, [options, query, searchable]);
    const chosen = values
        .map((value) => options.find((option) => option.value === value)?.label ?? value)
        .filter((one) => one !== '');
    const summary = chosen.length === 0
        ? placeholder
        : chosen.length === 1
            ? (chosen[0] ?? '')
            : `${chosen[0] ?? ''} +${String(chosen.length - 1)}`;
    const toggle = (option) => {
        if (option.disabled === true)
            return;
        anchor.current = option.value;
        onValuesChange(values.includes(option.value)
            ? values.filter((value) => value !== option.value)
            : [...values, option.value]);
    };
    /**
     * `rangeSelect` · everything from the anchor to `option`, in the order shown, ADDED to the choice
     * — the file-list convention. Without an anchor in view it is an ordinary toggle.
     */
    const extend = (option) => {
        const from = shown.findIndex((one) => one.value === anchor.current);
        const to = shown.findIndex((one) => one.value === option.value);
        if (from === -1 || to === -1) {
            toggle(option);
            return;
        }
        const span = shown
            .slice(Math.min(from, to), Math.max(from, to) + 1)
            .filter((one) => one.disabled !== true)
            .map((one) => one.value);
        onValuesChange([...values, ...span.filter((value) => !values.includes(value))]);
        anchor.current = option.value;
    };
    const onListKey = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActive((at) => Math.min(shown.length - 1, at + 1));
        }
        else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActive((at) => Math.max(0, at - 1));
        }
        else if (event.key === 'Enter' || (event.key === ' ' && !searchable)) {
            event.preventDefault();
            const option = shown[active];
            if (option !== undefined)
                toggle(option);
        }
        else if (event.key === 'Escape') {
            event.preventDefault();
            close();
            trigger.current?.querySelector('button')?.focus();
        }
    };
    return ((0, jsx_runtime_1.jsxs)("div", { ref: trigger, className: "relative inline-flex w-full min-w-0", children: [(0, jsx_runtime_1.jsxs)("button", { type: "button", id: id, role: "combobox", "aria-label": label, "aria-expanded": open, "aria-controls": open ? listId : undefined, "aria-haspopup": "listbox", disabled: disabled, onClick: () => {
                    setOpen((was) => !was);
                }, onKeyDown: (event) => {
                    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setOpen(true);
                    }
                }, className: (0, cn_1.cn)('focus-visible:border-ring focus-visible:ring-ring/50 flex w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-md border bg-transparent text-left transition-[color,background-color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50', inline
                    ? 'hover:border-input hover:bg-background h-[30px] border-transparent px-2 py-1 text-[12.5px] shadow-none'
                    : 'border-input h-8 px-2.5 py-1 text-sm shadow-xs', className), children: [summarize !== undefined ? ((0, jsx_runtime_1.jsx)("span", { className: "flex min-w-0 flex-1 items-center gap-1.5 truncate", children: summarize(values) })) : ((0, jsx_runtime_1.jsx)(truncated_text_1.TruncatedText, { className: (0, cn_1.cn)('flex-1', chosen.length === 0 ? 'text-muted-foreground' : ''), tooltip: chosen.join(', '), children: summary })), (0, jsx_runtime_1.jsx)(lucide_react_1.ChevronDown, { size: 14, "aria-hidden": "true", className: "shrink-0 opacity-50" })] }), open
                ? (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsxs)("div", { ref: surface, style: (0, overlay_1.anchorTo)(rect, {
                        width: panelWidth ?? Math.max(rect?.width ?? 220, 176),
                    }), className: "pop-in bg-popover text-popover-foreground shadow-pop overflow-hidden rounded-md border", children: [searchable ? ((0, jsx_runtime_1.jsx)("div", { className: "border-b p-1", children: (0, jsx_runtime_1.jsx)("input", { autoFocus: true, value: query, placeholder: searchPlaceholder, "aria-label": searchPlaceholder, onChange: (event) => {
                                    setQuery(event.target.value);
                                    setActive(0);
                                }, onKeyDown: onListKey, className: "h-7 w-full bg-transparent px-2 text-sm outline-none" }) })) : null, hint === undefined ? null : ((0, jsx_runtime_1.jsx)("p", { className: "text-muted-foreground border-b px-3 py-1.5 text-[11.5px]", children: hint })), selectAllLabel === undefined || deselectAllLabel === undefined ? null : ((0, jsx_runtime_1.jsx)(select_all_bar_1.SelectAllBar, { shown: shown
                                .filter((option) => option.disabled !== true)
                                .map((option) => option.value), chosen: values, onChange: (next) => {
                                anchor.current = null;
                                onValuesChange(next);
                            }, selectAll: selectAllLabel, deselectAll: deselectAllLabel })), clearLabel === undefined ? null : ((0, jsx_runtime_1.jsxs)("button", { type: "button", onClick: () => {
                                anchor.current = null;
                                onValuesChange([]);
                            }, className: "hover:bg-accent flex w-full items-center gap-2 border-b px-3 py-1.5 text-left text-sm outline-none", children: [checkboxes ? (0, jsx_runtime_1.jsx)(Box, { on: values.length === 0 }) : null, (0, jsx_runtime_1.jsx)("span", { className: "flex-1", children: clearLabel }), !checkboxes && values.length === 0 ? ((0, jsx_runtime_1.jsx)(lucide_react_1.Check, { size: 14, "aria-hidden": "true", className: "shrink-0" })) : null] })), (0, jsx_runtime_1.jsx)("div", { id: listId, role: "listbox", "aria-multiselectable": "true", tabIndex: searchable ? -1 : 0, autoFocus: !searchable, onKeyDown: onListKey, className: "max-h-60 overflow-y-auto p-1 outline-none", children: shown.length === 0 ? ((0, jsx_runtime_1.jsx)("p", { className: "text-muted-foreground px-2 py-1.5 text-sm", children: emptyLabel })) : (shown.map((option, at) => ((0, jsx_runtime_1.jsxs)("button", { type: "button", role: "option", "data-value": option.value, "aria-selected": values.includes(option.value), disabled: option.disabled === true, onMouseEnter: () => {
                                    setActive(at);
                                }, onClick: (event) => {
                                    if (rangeSelect && event.shiftKey)
                                        extend(option);
                                    else
                                        toggle(option);
                                }, className: (0, cn_1.cn)('flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none', at === active ? 'bg-accent text-accent-foreground' : '', option.disabled === true ? 'cursor-not-allowed opacity-50' : ''), children: [checkboxes ? (0, jsx_runtime_1.jsx)(Box, { on: values.includes(option.value) }) : null, (0, jsx_runtime_1.jsx)(truncated_text_1.TruncatedText, { className: "flex-1", children: option.label }), option.hint === undefined ? null : ((0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground shrink-0 text-[11.5px] tabular-nums", children: option.hint })), !checkboxes && values.includes(option.value) ? ((0, jsx_runtime_1.jsx)(lucide_react_1.Check, { size: 14, "aria-hidden": "true", className: "shrink-0" })) : null] }, option.value)))) })] }), document.body)
                : null] }));
}
/** `checkboxes` · the square at a row's front — the kit `Checkbox`'s look, drawn, not an input. */
function Box({ on }) {
    return ((0, jsx_runtime_1.jsx)("span", { "aria-hidden": "true", className: (0, cn_1.cn)('grid size-4 shrink-0 place-items-center rounded-[4px] border', on ? 'bg-primary border-primary text-primary-foreground' : 'border-input'), children: on ? (0, jsx_runtime_1.jsx)(lucide_react_1.Check, { size: 11, strokeWidth: 3 }) : null }));
}
//# sourceMappingURL=multi-select.js.map